import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, ArrowLeft } from "lucide-react";

// Mock data (can be moved to a shared location if used elsewhere)
const mockCategories = [
  { id: 1, name: "Mua bán học", icon: null },
  { id: 2, name: "Tưới cây hộ", icon: null },
  { id: 3, name: "Mượn quạt", icon: null },
  { id: 4, name: "Mượn chổi", icon: null },
];

const mockExploreCategories = [
  {
    id: 1,
    name: "Mua bán, săn sale",
    image: "images/marketplace.png",
    link: "/search/marketplace",
  },
  {
    id: 2,
    name: "Hội nhóm",
    image: "images/groups.png",
    link: "/search/groups",
  },
  {
    id: 3,
    name: "Địa điểm yêu thích",
    image: "images/favoriteplace.png",
    link: "/search/places",
  },
  {
    id: 4,
    name: "Sự kiện",
    image: "images/events.png",
    link: "/search/events",
  },
];

interface SearchOverlayProps {
  onClose: () => void;
}

export const SearchOverlay: React.FC<SearchOverlayProps> = ({ onClose }) => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [searchCategory, setSearchCategory] = useState<string | null>(null);

  const handleBackFromSearch = () => {
    if (searchCategory) {
      setSearchCategory(null); // Go back to main search view from category specific search
    } else {
      onClose(); // Close the entire search overlay
    }
  };

  const handleCategoryNavigation = (path: string, categoryName?: string) => {
    if (categoryName === "Hội nhóm") navigate("/groups");
    else if (categoryName === "Sự kiện") navigate("/events");
    else if (categoryName === "Địa điểm yêu thích")
      navigate("/favorite-places");
    else if (categoryName === "Mua bán săn sale") navigate("/marketplace");
    else if (path.startsWith("/search/")) setSearchCategory(path.split("/")[2]);
    else navigate(path);

    // Close overlay after navigation for direct page links
    if (categoryName) onClose();
  };

  const renderSearchContent = () => {
    // If a specific category search is active (e.g., user clicked "Marketplace" from explore)
    // This part might need further refinement based on how category-specific search results are displayed
    if (searchCategory) {
      // For now, let's assume direct navigation for these categories, handled by handleCategoryNavigation
      // If we wanted a category-specific search UI within the overlay, it would go here.
      // For simplicity, we'll navigate away or show a placeholder.
      if (
        ["marketplace", "groups", "places", "events"].includes(searchCategory)
      ) {
        // Already navigated, this content won't be shown if onClose was called.
        // If onClose is not called, this is a placeholder.
        return <div className="p-4">Đang tìm trong {searchCategory}...</div>;
      }
    }

    // Default search view (trending and explore)
    return (
      <div className="px-6 py-4">
        <h2 className="text-xl text-[#1E3A3A] font-medium mb-4">Xu hướng</h2>
        <div className="flex flex-wrap gap-2 mb-6">
          {mockCategories.map((category) => (
            <button
              key={category.id}
              className="px-4 py-2 bg-[#FBF6F1] rounded-[6px] text-sm text-[#1E3A3A] border-1 border-[#F4E7DA]"
              // Potentially make these trigger a search with the category name
              // onClick={() => setSearchQuery(category.name)}
            >
              {category.name}
            </button>
          ))}
        </div>

        <h2 className="text-xl text-[#1E3A3A] font-medium mb-4">
          Hoặc khám phá các nội dung sau
        </h2>
        <div className="grid grid-cols-2 gap-4">
          {mockExploreCategories.map((category) => (
            <div
              key={category.id}
              className="relative rounded-[6px] overflow-hidden cursor-pointer"
              onClick={() =>
                handleCategoryNavigation(category.link, category.name)
              }
            >
              <img
                src={category.image}
                alt={category.name}
                className="w-full h-32 object-cover"
              />
              <div className="absolute inset-0 bg-black/30 flex items-end pb-3 pl-2">
                <span className="text-[#F4E7DA] font-[16px] font-manrope leading-none">
                  {category.name}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Special case for places - don't show the search header, navigate immediately
  if (searchCategory === "places") {
    // Navigation is handled by handleCategoryNavigation, this component instance would be closed.
    // This check might be redundant if onClose is called immediately.
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 bg-[#FBF6F1] flex flex-col animate-in slide-in-from-bottom duration-300">
      <div className="p-4 border-b border-[#D1D1D0] flex items-center">
        <button onClick={handleBackFromSearch} className="p-2 mr-2">
          <ArrowLeft className="h-5 w-5 text-[#1E3A3A]" />
        </button>
        <div className="flex-1 relative">
          <div className="w-full bg-white rounded-[5px] flex items-center px-3 py-1.5">
            <Search className="h-4 w-4 text-[#6F6F6B] mr-2" />
            <input
              type="text"
              placeholder={
                searchCategory === "marketplace"
                  ? "Tìm kiếm sản phẩm..."
                  : searchCategory === "groups"
                    ? "Tìm kiếm hội nhóm"
                    : searchCategory === "events"
                      ? "Tìm kiếm sự kiện"
                      : "Tìm kiếm"
              }
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="bg-transparent border-none outline-none text-sm w-full text-[#6F6F6B] font-normal"
              autoFocus
              // Add onKeyPress or a search button to trigger actual search based on searchQuery
            />
          </div>
        </div>
      </div>
      <div className="flex-1 overflow-auto">{renderSearchContent()}</div>
    </div>
  );
};
